import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Divider,
  Paper,
} from '@mui/material';
// [LEGACY] DownloadIcon was used by the conversation detail Actions column (View Flow / Download).
// Uncomment when that functionality is revived.
// import DownloadIcon from '@mui/icons-material/Download';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpdateIcon from '@mui/icons-material/Update';
import MessageIcon from '@mui/icons-material/Message';
import ForumIcon from '@mui/icons-material/Forum';
import ReplyIcon from '@mui/icons-material/Reply';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import { useAdminApi } from '../hooks/useAdminApi';
import {
  UserDetailResponse,
  UserConversationListResponse,
  ConversationSummaryItem,
} from '../types/adminApiTypes';
import { formatDate, formatNumber } from '../utils/helpers';

const UserDetailView: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();

  // State for user details
  const { data: userData, loading: userLoading, error: userError, fetchData: fetchUserData } = useAdminApi<UserDetailResponse>();

  // State for conversations
  const [showConversations, setShowConversations] = useState<boolean>(false);
  const [conversationPaginationModel, setConversationPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  const { data: convData, loading: convLoading, error: convError, fetchData: fetchConvData } = useAdminApi<UserConversationListResponse>();
  const convCursorCacheRef = useRef<Record<number, string>>({});
  const lastFetchedConvPageRef = useRef<number>(0);
  // Store fetchConvData in a ref so auth token refreshes inside useAdminApi don't
  // change the function reference and trigger an unintended re-fetch.
  const fetchConvDataRef = useRef(fetchConvData);
  fetchConvDataRef.current = fetchConvData;
  // Keep row count stable while loading so MUI DataGrid doesn't auto-reset the page.
  // Prefers the exact Firestore COUNT from the backend; falls back to a page-based
  // estimate until the backend field is available.
  const [convRowCountState, setConvRowCountState] = useState(0);
  useEffect(() => {
    if (convData !== null) {
      if (convData.total_current_conversations != null) {
        setConvRowCountState(convData.total_current_conversations);
      } else {
        const pageBase = lastFetchedConvPageRef.current * conversationPaginationModel.pageSize;
        setConvRowCountState(pageBase + convData.conversations.length + (convData.next_cursor ? 1 : 0));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convData]);

  // [LEGACY] Conversation detail download + View Flow actions are disabled while the
  // conversation detail endpoint and flow visualization are being reworked.
  // Re-enable once the backend endpoint and ConversationDetailFlowView are updated
  // to work with the unified graph_conversations collection.
  //
  // const [downloadingConvId, setDownloadingConvId] = useState<string | null>(null);
  // const {
  //   data: downloadData,
  //   loading: downloadLoading,
  //   error: downloadError,
  //   fetchData: fetchDownloadData,
  // } = useAdminApi<ConversationDetailResponse>();
  //
  // const handleDownloadConversationDetails = useCallback((rowData: ConversationSummaryItem) => {
  //   if (!userId || downloadLoading) return;
  //   setDownloadingConvId(rowData.id);
  //   fetchDownloadData({
  //     endpoint: `/conversations/${rowData.id}/details`,
  //     user_id: userId,
  //     conversation_type: rowData.thinkingType,
  //   });
  // }, [userId, fetchDownloadData, downloadLoading]);
  //
  // useEffect(() => {
  //   if (downloadData && downloadingConvId && downloadData.conversation_id === downloadingConvId) {
  //     try {
  //       const jsonString = JSON.stringify(downloadData.process_details || [], null, 2);
  //       const blob = new Blob([jsonString], { type: 'application/json' });
  //       const url = window.URL.createObjectURL(blob);
  //       const link = document.createElement('a');
  //       link.href = url;
  //       link.download = `conversation_${downloadingConvId}_details.json`;
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //       window.URL.revokeObjectURL(url);
  //     } catch (err) {
  //       console.error("[UserDetailView] Error creating or triggering download:", err);
  //     } finally {
  //       setDownloadingConvId(null);
  //     }
  //   } else if (downloadError && downloadingConvId) {
  //     setDownloadingConvId(null);
  //   }
  // }, [downloadData, downloadingConvId, downloadError]);

  // Columns definition for conversations table
  const conversationColumns: GridColDef<ConversationSummaryItem>[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      valueGetter: (value) => value || 'Untitled',
    },
    {
      field: 'lastMessageText',
      headerName: 'Last Message Snippet',
      flex: 2,
      valueGetter: (value) => value || 'N/A',
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value as string | undefined}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'lastMessageTimestamp',
      headerName: 'Last Updated',
      width: 180,
      valueFormatter: (value) => formatDate(value, 'D MMM YYYY HH:mm'),
    },
    {
      field: 'messageCount',
      headerName: 'Msg Count',
      type: 'number',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: 'totalCost',
      headerName: 'Cost',
      type: 'number',
      width: 90,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number | null | undefined) =>
        value != null ? `$${value.toFixed(4)}` : 'N/A',
    },
    {
      field: 'processingTimeSecs',
      headerName: 'Proc. Time',
      type: 'number',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number | null | undefined) =>
        value != null ? `${value.toFixed(1)}s` : 'N/A',
    },
    // [LEGACY] "View Flow" and download actions are disabled while ConversationDetailFlowView
    // and the backend /conversations/{id}/details endpoint are being reworked for the
    // unified graph_conversations collection. Re-enable together with that work.
    //
    // {
    //   field: 'id',
    //   headerName: 'Actions',
    //   width: 180,
    //   sortable: false,
    //   renderCell: (params: GridRenderCellParams<any, ConversationSummaryItem>) => {
    //     const isCurrentlyDownloading = downloadingConvId === params.row.id;
    //     return (
    //       <Stack direction="row" spacing={1}>
    //         <Button
    //           variant="outlined"
    //           size="small"
    //           component={RouterLink}
    //           to={`/dashboard/users/${userId}/conversations/${params.row.id}/${params.row.thinkingType}`}
    //         >
    //           View Flow
    //         </Button>
    //         <IconButton
    //           aria-label="Download conversation details"
    //           size="small"
    //           onClick={() => handleDownloadConversationDetails(params.row)}
    //           disabled={isCurrentlyDownloading || downloadLoading}
    //         >
    //           {isCurrentlyDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
    //         </IconButton>
    //       </Stack>
    //     );
    //   },
    // },
  ];

  // Fetch user details effect
  useEffect(() => {
    if (userId) {
      // TODO: Update to new endpoint once backend is ready
      // fetchUserData({ endpoint: `/users/${userId}/detail` });
      
      // Temporary: Use old endpoint until new one is deployed
      // We'll need to transform the response to match UserDetailResponse
      console.log('[UserDetailView] Fetching user details for:', userId);
      fetchUserData({ endpoint: `/users/${userId}/detail` });
    }
  }, [userId, fetchUserData]);

  // Reset cursor cache when conversations section is first shown.
  useEffect(() => {
    convCursorCacheRef.current = {};
    setConversationPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [showConversations]);

  // Main fetch effect for conversations.
  useEffect(() => {
    if (!userId || !showConversations) return;
    lastFetchedConvPageRef.current = conversationPaginationModel.page;

    const params: Record<string, any> = {
      endpoint: `/users/${userId}/conversations`,
      conversation_type: 'reflection',
      page_size: conversationPaginationModel.pageSize,
    };

    const cursor = conversationPaginationModel.page > 0
      ? convCursorCacheRef.current[conversationPaginationModel.page]
      : undefined;
    if (cursor) params.next_cursor = cursor;

    fetchConvDataRef.current(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, showConversations, conversationPaginationModel.page, conversationPaginationModel.pageSize]);

  // Cache the next cursor when a page of conversations arrives.
  useEffect(() => {
    if (convData?.next_cursor) {
      convCursorCacheRef.current[lastFetchedConvPageRef.current + 1] = convData.next_cursor;
    }
  }, [convData]);

  const handleConvPaginationModelChange = (newModel: GridPaginationModel) => {
    if (newModel.pageSize !== conversationPaginationModel.pageSize) {
      convCursorCacheRef.current = {};
      setConversationPaginationModel({ page: 0, pageSize: newModel.pageSize });
    } else if (newModel.page !== conversationPaginationModel.page) {
      setConversationPaginationModel(newModel);
    }
  };

  const handleShowConversations = () => {
    setShowConversations(true);
  };

  if (!userId) {
    return <Alert severity="warning">User ID not found in URL.</Alert>;
  }

  const loading = userLoading;
  const error = userError;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading user details: {error}</Alert>;
  }

  if (!userData) {
    return <Typography>No data available for this user.</Typography>;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Page Header */}
      <Typography variant="h5" gutterBottom component="div" sx={{ mb: 3 }}>
        User Details
      </Typography>

      {/* User Profile Card */}
      <Paper elevation={0} variant="outlined" sx={{ mb: 4, p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
          <Box sx={{
            width: 58, height: 58, borderRadius: '50%',
            bgcolor: 'primary.main', display: 'flex', alignItems: 'center',
            justifyContent: 'center', mr: 2.5, flexShrink: 0,
          }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.5rem' }}>
              {userData.email.charAt(0).toUpperCase()}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-all' }}>
              {userData.email}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {userData.organization || 'No organization'}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mb: 2.5 }} />
        <Grid container spacing={2}>
          {([
            { icon: <EmailIcon />, label: 'Email', value: userData.email, xs: 12, sm: 12, md: 5, wrap: true },
            { icon: <BusinessIcon />, label: 'Organization', value: userData.organization || 'N/A', xs: 12, sm: 4, md: 3, wrap: false },
            { icon: <PersonIcon />, label: 'User Type', value: userData.user_type, xs: 12, sm: 4, md: 2, wrap: false },
            { icon: <CalendarTodayIcon />, label: 'Member Since', value: formatDate(userData.created_at), xs: 12, sm: 4, md: 2, wrap: false },
          ] as const).map(({ icon, label, value, xs, sm, md, wrap }) => (
            <Grid item xs={xs} sm={sm} md={md} key={label}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ color: 'primary.main', mt: 0.4, flexShrink: 0 }}>{icon}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.4 }}>{label}</Typography>
                  <Typography variant="h6" sx={{
                    fontWeight: 500,
                    ...(wrap ? { wordBreak: 'break-all' } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
                  }}>
                    {value}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Usage Metrics Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          Usage Metrics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total User Messages"
              value={userData.total_user_messages}
              icon={<MessageIcon fontSize="large" />}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Chats"
              value={userData.total_chats}
              icon={<ForumIcon fontSize="large" />}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Follow-ups"
              value={userData.total_follow_ups}
              icon={<ReplyIcon fontSize="large" />}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Last Activity"
              value={userData.last_activity ? formatDate(userData.last_activity, 'D MMM YYYY HH:mm') : 'N/A'}
              icon={<UpdateIcon fontSize="large" />}
              formatType="string"
              loading={loading}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Cost Breakdown Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          Cost Breakdown
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Costs"
              value={userData.total_costs}
              icon={<AttachMoneyIcon fontSize="large" />}
              formatType="currency"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="OpenAI Costs"
              value={userData.total_openai_costs}
              icon={<AttachMoneyIcon fontSize="large" />}
              formatType="currency"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Google Costs"
              value={userData.total_google_costs}
              icon={<AttachMoneyIcon fontSize="large" />}
              formatType="currency"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Other LLM Costs"
              value={userData.total_other_llm_costs}
              icon={<AttachMoneyIcon fontSize="large" />}
              formatType="currency"
              loading={loading}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Conversations Section */}
      <Typography variant="h5" gutterBottom component="div">
        User Chats
      </Typography>
      {!showConversations && (
        <Button variant="contained" onClick={handleShowConversations} sx={{ mb: 2 }}>
          Load All Chats
        </Button>
      )}

      {showConversations && (
        <Paper sx={{ p: 2 }}>
          {convError && <Alert severity="error" sx={{ mb: 2 }}>Error loading conversations: {convError}</Alert>}

          <DataTable
            rows={convData?.conversations || []}
            columns={conversationColumns}
            loading={convLoading}
            paginationModel={conversationPaginationModel}
            onPaginationModelChange={handleConvPaginationModelChange}
            rowCount={convRowCountState}
            paginationMode="server"
            getRowId={(row) => row.id}
          />
        </Paper>
      )}

    </Box>
  );
};

export default UserDetailView;
